import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface CreditTransaction {
  id: string;
  credits_change: number;
  transaction_type: string;
  description: string | null;
  reference_id: string | null;
  created_at: string;
}

export interface CreditPackage {
  id: string;
  name: string;
  credits: number;
  price_cents: number;
  description: string | null;
  display_order: number;
}

export const useCredits = () => {
  const [credits, setCredits] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const { toast } = useToast();

  const fetchCredits = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile, error } = await supabase
        .from('profiles')
        .select('credits')
        .eq('user_id', user.id)
        .single();

      if (error) {
        console.error('Error fetching credits:', error);
        return;
      }

      setCredits(profile?.credits || 0);
    } catch (error) {
      console.error('Error fetching credits:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('credit_transactions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) {
        console.error('Error fetching transactions:', error);
        return;
      }

      setTransactions(data || []);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  };

  /**
   * @deprecated Ces fonctions sont obsolètes depuis l'implémentation du système de sécurité des crédits.
   * Les crédits ne peuvent plus être modifiés côté client pour des raisons de sécurité.
   * 
   * Utilisez plutôt :
   * - Edge functions avec service_role pour les déductions (ex: generate-artwork)
   * - Edge functions avec service_role pour les ajouts (ex: process-credit-payment)
   * 
   * Ces fonctions retournent maintenant toujours false car les triggers de base de données
   * empêchent les modifications de crédits depuis le contexte client.
   */

  const deductCredits = async (amount: number, description: string, referenceId?: string) => {
    console.warn('deductCredits is deprecated - credits can only be modified server-side for security');
    toast({
      title: "Opération non autorisée",
      description: "Les crédits ne peuvent être modifiés que côté serveur pour des raisons de sécurité.",
      variant: "destructive",
    });
    return false;
  };

  const addCredits = async (amount: number, type: string, description: string, referenceId?: string) => {
    console.warn('addCredits is deprecated - credits can only be modified server-side for security');
    toast({
      title: "Opération non autorisée", 
      description: "Les crédits ne peuvent être modifiés que côté serveur pour des raisons de sécurité.",
      variant: "destructive",
    });
    return false;
  };

  useEffect(() => {
    fetchCredits();
    fetchTransactions();
  }, []);

  // Refetch credits when tab becomes visible (e.g., returning from Stripe payment tab)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchCredits();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  return {
    credits,
    loading,
    transactions,
    fetchCredits,
    fetchTransactions,
    deductCredits,
    addCredits,
  };
};